import type { HealthBand } from '@mvcp/contracts';

/**
 * A band-colored progress bar (0–100) for the Overall Score (Story 4.1, UX-DR13).
 * The fill tone follows the engine `band` (never color alone — the numeric score
 * label lives beside it in the headline). Carries an `aria-label` restating the
 * percentage so it is not a hover-only/visual-only signal, and exposes the
 * `progressbar` role with the standard `aria-valuenow/min/max`.
 *
 * A `null` percentage (nothing assessed) renders an empty neutral track and reads
 * "not yet scored".
 *
 * `label` names WHICH score the bar carries (Story 7.2). It defaults to
 * `'Overall score'` so every pre-existing call site is unchanged; a surface showing
 * two different scores (a Site's own score beside the fleet's) must pass its own name,
 * or a screen reader announces two different numbers under one identity.
 */
export function ProgressBar({
  percentage,
  band,
  label = 'Overall score',
}: {
  percentage: number | null;
  band: HealthBand;
  label?: string;
}): React.JSX.Element {
  const pct = percentage === null ? 0 : Math.max(0, Math.min(100, percentage));
  const rounded = percentage === null ? null : Math.round(percentage);
  const ariaLabel =
    rounded === null ? `${label} not yet scored` : `${label} ${rounded} percent`;

  return (
    <div
      className="progress-bar"
      data-tone={band}
      role="progressbar"
      aria-label={ariaLabel}
      aria-valuenow={rounded ?? undefined}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div className="progress-bar__fill" style={{ width: `${pct}%` }} />
    </div>
  );
}
